import type {
  PiV2Alert,
  PiV2Appointment,
  PiV2Bundle,
  PiV2Call,
  PiV2Claim,
  PiV2Note,
  PiV2Opportunity,
  PiV2Plan,
  PiV2TreatmentPlan,
  PiV2VerificationSource,
} from './typesV2';

/**
 * v2 insurance fixtures.
 *
 * Deliberately covers the cases the v1 mock could not express:
 * — Sara carries **three** plans, so the tertiary path is exercised.
 * — Tom's eligibility check **failed**, so the estimate gate renders its
 *   honest empty state rather than a fabricated number.
 * — James has **one** plan, so the single-plan layout is real rather than a
 *   segmented-control setting.
 * — Sara's appointment sits **outside** the 7-day window, so the visit band is
 *   absent for her and the page starts at the plan cards.
 *
 * Appointment dates are generated relative to today rather than hard-coded, so
 * the window rule is actually exercised whenever the prototype is opened.
 */

const DAY_FMT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

function appointmentIn(
  daysAway: number,
  hour: number,
  minute: number,
  provider: string,
  procedureLabel: string,
): PiV2Appointment {
  const d = new Date();
  d.setDate(d.getDate() + daysAway);
  d.setHours(hour, minute, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:${pad(minute)}:00`;
  return {
    id: `appt-${String(daysAway)}`,
    at: iso,
    whenLabel: `${DAY_FMT.format(d)} · ${TIME_FMT.format(d)}`,
    provider,
    procedureLabel,
  };
}

const SUNLIFE: PiV2Plan = {
  id: 'pl-sunlife',
  role: 'primary',
  carrier: 'Sunlife',
  planName: 'Dental PPO Plus',
  planType: 'PPO',
  inNetwork: true,
  memberId: 'SL-442918305',
  group: '88421',
  subscriber: 'Amara Reza',
  relationship: 'Self',
  effective: 'Jan 1, 2024',
  carrierPhone: '(800) 555-0199',
  accent: '#3E6FD8',
  eligibility: { state: 'ok', checkedAt: 'Aug 14, 2026' },
  benefits: {
    annualMax: 1500,
    used: 1160,
    remaining: 340,
    deductible: 50,
    deductibleMet: 50,
    familyDeductible: 150,
    familyMet: 100,
    planYearEnd: 'Dec 31, 2026',
    daysLeftInPlanYear: 138,
  },
  coverage: [
    { category: 'Preventive', percent: '100%', state: 'covered', deductibleWaived: true, detail: 'Cleanings, exams, X-rays, fluoride' },
    { category: 'Basic', percent: '80%', state: 'covered', detail: 'Fillings, simple extractions, perio therapy' },
    { category: 'Major', percent: '50%', state: 'covered', detail: 'Crowns, bridges, dentures, root canals' },
    { category: 'Orthodontics', percent: '50%', state: 'covered', detail: 'Lifetime max $1,500 · age 19 & under' },
    { category: 'Implants', state: 'not_covered', detail: 'Plan exclusion — patient pays 100%' },
    { category: 'Sealants', state: 'unknown', detail: 'Payer did not return a benefit for this service' },
  ],
  limitations: [
    { label: 'Missing tooth clause', detail: 'Teeth missing before coverage start not covered for 12 months' },
    { label: 'Waiting period', detail: 'None. Active coverage > 12 months' },
    { label: 'Downgrade', detail: 'Posterior composites paid at the amalgam rate' },
  ],
  frequency: [
    {
      group: 'Preventive',
      percent: '100%',
      rows: [
        { procedure: 'Prophylaxis (D1110)', limit: '2 per calendar year', last: 'Nov 13, 2025', next: 'Eligible now', status: 'Eligible', eligible: true },
        { procedure: 'Bitewing X-rays (D0274)', limit: '1 per calendar year', last: 'Nov 13, 2025', next: 'Eligible now', status: 'Eligible', eligible: true },
        { procedure: 'Fluoride (D1208)', limit: '1 per calendar year', next: 'Eligible now', status: 'Eligible', eligible: true },
      ],
    },
    {
      group: 'Major',
      percent: '50%',
      rows: [
        { procedure: 'Crown (D2740)', limit: '1 per tooth / 5 years', last: 'Apr 23, 2026', next: 'Apr 2031', status: 'Not yet eligible', eligible: false },
      ],
    },
  ],
  preauth: [
    { id: 'PA-88213', procedure: 'Crown — tooth #14', status: 'approved', detail: 'Approved Apr 2, 2026 · valid 90 days' },
    { id: 'PA-90114', procedure: 'Night guard (D9944)', status: 'pending', detail: 'Submitted Aug 3, 2026' },
  ],
};

const METLIFE: PiV2Plan = {
  id: 'pl-metlife',
  role: 'secondary',
  carrier: 'MetLife',
  planName: 'Dental HMO Select',
  planType: 'DHMO',
  inNetwork: true,
  memberId: 'ML-778201445',
  group: '00912',
  subscriber: 'Jane Brooks',
  relationship: 'Spouse',
  effective: 'Jan 1, 2025',
  carrierPhone: '(800) 638-5433',
  accent: '#B8862F',
  eligibility: { state: 'ok', checkedAt: 'Aug 14, 2026' },
  benefits: {
    annualMax: 1000,
    used: 180,
    remaining: 820,
    deductible: 0,
    deductibleMet: 0,
    familyDeductible: 0,
    familyMet: 0,
    planYearEnd: 'Dec 31, 2026',
    daysLeftInPlanYear: 138,
  },
  coverage: [
    { category: 'Preventive', percent: '100%', state: 'covered', deductibleWaived: true, detail: 'In-network general dentist only' },
    { category: 'Basic', percent: '70%', state: 'covered', detail: 'Fillings, extractions' },
    { category: 'Major', percent: '40%', state: 'covered', detail: 'Crowns, bridges — 6 month wait served' },
    { category: 'Orthodontics', state: 'not_covered', detail: 'Adult ortho excluded on this plan' },
    { category: 'Implants', state: 'not_covered', detail: 'Plan exclusion' },
    { category: 'Sealants', percent: '100%', state: 'covered', detail: 'Dependants under 16' },
  ],
  limitations: [
    { label: 'Assigned dentist', detail: 'Benefits payable only at the assigned DHMO facility' },
    { label: 'Coordination', detail: 'Pays as secondary — balance only, up to the plan allowance' },
  ],
  frequency: [
    {
      group: 'Preventive',
      percent: '100%',
      rows: [
        { procedure: 'Prophylaxis (D1110)', limit: '2 per calendar year', last: 'Nov 13, 2025', next: 'Eligible now', status: 'Eligible', eligible: true },
      ],
    },
  ],
  preauth: [],
};

const AMARA_OPPORTUNITIES: PiV2Opportunity[] = [
  {
    id: 'op-1',
    itemType: 'INSURANCE_TIMING',
    title: 'Unused benefits + unscheduled treatment',
    tier: 'T1',
    detail: '$340 in annual benefits remaining · $2,770 unscheduled treatment on file',
    moneyLabel: 'Est. insurance may pay',
    money: 340,
    script:
      'You still have insurance benefits available this year. We can help you use them before they reset.',
    icon: 'dollar',
  },
  {
    id: 'op-2',
    itemType: 'FINANCIAL_AWARENESS',
    title: 'Benefits reset Dec 31',
    tier: 'T2',
    detail: '$340 remaining · plan resets in 138 days',
    moneyLabel: 'Potential loss if unused',
    money: 340,
    script:
      'Your benefits reset on December 31. Scheduling before then can help you get the most from your plan.',
    icon: 'calendar',
  },
  {
    id: 'op-3',
    itemType: 'HYGIENE_RECALL',
    title: 'Fluoride eligible now',
    tier: 'T2',
    detail: 'Covered at 100% on Sunlife · 1 per calendar year, none used',
    moneyLabel: 'Est. patient cost',
    money: 0,
    script: 'Your insurance covers fluoride today if you would like to add it.',
    icon: 'shield',
  },
  {
    id: 'op-4',
    itemType: 'FAMILY_SCHEDULING',
    title: 'Family member overdue',
    tier: 'T3',
    detail: 'Jane Brooks is 15 days past her recall date',
    moneyLabel: 'Est. production',
    money: 210,
    script: 'While you are here — would you like me to book Jane in as well?',
    icon: 'team',
  },
];

const AMARA_TREATMENT: PiV2TreatmentPlan = {
  total: 2770,
  insurance: 1340,
  patient: 1430,
  rows: [
    { procedure: 'Crown — tooth #19 (D2740)', total: 1450, insurance: 725, patient: 725, status: 'Not scheduled', scheduled: false },
    { procedure: 'Night guard (D9944)', total: 620, insurance: 310, patient: 310, status: 'Pre-auth pending', scheduled: false },
    { procedure: 'Composite filling ×2 (D2392)', total: 700, insurance: 305, patient: 395, status: 'Scheduled May 28', scheduled: true },
  ],
};

const AMARA_CLAIMS: PiV2Claim[] = [
  { id: 'CLM-772391', date: 'Apr 23, 2026', procedure: 'Crown — porcelain (D2740)', billed: 1450, allowed: 1120, insurance: 560, patient: 560, status: 'paid', statusLabel: 'Paid' },
  { id: 'CLM-761204', date: 'Apr 23, 2026', procedure: 'Core buildup (D2950)', billed: 280, allowed: 210, insurance: 105, patient: 105, status: 'paid', statusLabel: 'Paid' },
  { id: 'CLM-758830', date: 'Mar 30, 2026', procedure: 'Periapical film (D0220)', billed: 42, allowed: 31, insurance: 31, patient: 0, status: 'paid', statusLabel: 'Paid' },
  { id: 'CLM-801442', date: 'Aug 3, 2026', procedure: 'Night guard (D9944)', billed: 620, allowed: null, insurance: null, patient: null, status: 'pending', statusLabel: 'In review' },
  { id: 'CLM-744019', date: 'Nov 13, 2025', procedure: 'Implant consult (D6190)', billed: 180, allowed: 0, insurance: 0, patient: 180, status: 'denied', statusLabel: 'Denied — exclusion' },
];

const AMARA_ALERTS: PiV2Alert[] = [
  { id: 'al-max', severity: 'warning', text: 'Only $340 annual maximum remaining on Sunlife' },
  { id: 'al-cob', severity: 'warning', text: 'Secondary coordination (MetLife DHMO) not confirmed' },
  { id: 'al-seal', severity: 'info', text: 'Sealants benefit was not returned electronically' },
];

const AMARA_SOURCES: PiV2VerificationSource[] = [
  { source: 'Stedi eligibility', lastChecked: 'Aug 14, 2026 · 9:32 AM', result: 'Successful', detail: '271 response' },
  { source: 'ELVA insurance call', lastChecked: 'Aug 14, 2026 · 9:41 AM', result: 'Completed', detail: 'Crown frequency + downgrade' },
  { source: 'Payer manuals / documents', lastChecked: 'Aug 10, 2026', result: 'Current', detail: 'Sunlife 2026 Provider Manual' },
  { source: 'PMS & claim history', lastChecked: 'Live / current sync', result: 'Available', detail: 'Patient treatment and claim history' },
];

const AMARA_CALLS: PiV2Call[] = [
  {
    id: 'call-8839201',
    payer: 'Sunlife',
    at: 'Aug 14, 2026 · 9:41 AM',
    durationLabel: '4m 18s',
    reason: 'Crown frequency + downgrade',
    status: 'completed',
    statusLabel: 'Completed',
    calledNumber: '(800) 555-2273',
    representative: 'Denise R.',
    referenceNumber: 'SL-8839201',
    summary:
      'ELVA called Sunlife to verify crown frequency and downgrade rules. Sunlife confirmed D2740 is covered at 50%, limited to once per tooth every 5 years. No downgrade applies. Pre-authorization is not required.',
    transcript: [
      { speaker: 'ivr', text: 'Thank you for calling Sunlife provider services. Please say or enter the member ID.' },
      { speaker: 'elva', text: 'Member ID Sierra-Lima four four seven one nine zero two.' },
      { speaker: 'representative', text: 'This is Denise. How can I help you today?' },
      { speaker: 'elva', text: 'I’m verifying crown coverage and frequency for a patient — procedure D2740.' },
      { speaker: 'representative', text: 'D2740 is covered at 50%, once per tooth every 5 years. No alternate-benefit downgrade, and pre-auth is not required.' },
      { speaker: 'elva', text: 'Thank you — that’s everything I needed.' },
    ],
  },
  {
    id: 'call-7710044',
    payer: 'Sunlife',
    at: 'Jul 22, 2026 · 2:08 PM',
    durationLabel: '3m 06s',
    reason: 'Implant coverage',
    status: 'completed',
    statusLabel: 'Completed',
    calledNumber: '(800) 555-2273',
    representative: 'Marcus T.',
    referenceNumber: 'SL-7710044',
    summary:
      'ELVA verified implant coverage. Sunlife stated implants are covered at 50% subject to the missing-tooth clause; a predetermination is recommended before treatment.',
  },
  {
    id: 'call-6650121',
    payer: 'MetLife',
    at: 'Jun 10, 2026 · 11:15 AM',
    durationLabel: '5m 41s',
    reason: 'COB verification',
    status: 'partial',
    statusLabel: 'Partial',
    calledNumber: '(800) 555-9987',
    referenceNumber: 'ML-6650121',
    summary:
      'ELVA attempted to confirm coordination-of-benefits order. MetLife representative could not confirm secondary payment rules for this DHMO plan and advised submitting the primary EOB first.',
  },
];

const AMARA_NOTES: PiV2Note[] = [
  {
    id: 'note-1',
    at: 'Aug 21, 2026',
    staff: 'Jessica',
    text: 'Confirmed posterior composites downgrade to amalgam rate with Sunlife.',
    reference: '883920',
  },
  {
    id: 'note-2',
    at: 'Jul 30, 2026',
    staff: 'Marcus',
    text: 'Patient asked to be billed after secondary adjudication — do not collect estimated patient portion up front.',
  },
];

const AMARA: PiV2Bundle = {
  plans: [SUNLIFE, METLIFE],
  coordinationNote:
    'Sunlife PPO is primary; MetLife DHMO is secondary. Claims auto-route to primary first, then balance to secondary.',
  visit: {
    appointment: appointmentIn(3, 14, 30, 'Dr. Carter', 'Hygiene + perio eval'),
    opportunities: AMARA_OPPORTUNITIES,
    estimate: {
      estimateIsReal: true,
      lines: [
        { code: 'D1110', label: 'Prophylaxis — adult', officeFee: 145, insuranceEstimate: 145, patientEstimate: 0 },
        { code: 'D0274', label: 'Bitewings — four films', officeFee: 82, insuranceEstimate: 82, patientEstimate: 0 },
        { code: 'D2392', label: 'Composite — two surfaces', officeFee: 350, insuranceEstimate: 152, patientEstimate: 198 },
      ],
      totals: { officeFee: 577, insuranceEstimate: 379, patientEstimate: 198 },
      script:
        'Your estimated portion for this visit is $198. Your insurance is expected to cover approximately $379.',
    },
  },
  treatmentPlan: AMARA_TREATMENT,
  claims: AMARA_CLAIMS,
  alerts: AMARA_ALERTS,
  verificationSources: AMARA_SOURCES,
  calls: AMARA_CALLS,
  notes: AMARA_NOTES,
};

const JAMES_PLAN: PiV2Plan = {
  id: 'pl-cigna',
  role: 'primary',
  carrier: 'Cigna',
  planName: 'Dental Care Access',
  planType: 'PPO',
  inNetwork: true,
  memberId: 'CG-330192884',
  group: '41200',
  subscriber: 'James Chen',
  relationship: 'Self',
  effective: 'Jul 1, 2025',
  carrierPhone: '(800) 244-6224',
  accent: '#2F7D6B',
  eligibility: { state: 'ok', checkedAt: 'Aug 15, 2026' },
  benefits: {
    annualMax: 2000,
    used: 660,
    remaining: 1340,
    deductible: 50,
    deductibleMet: 50,
    familyDeductible: 150,
    familyMet: 50,
    planYearEnd: 'Jun 30, 2027',
    daysLeftInPlanYear: 319,
  },
  coverage: [
    { category: 'Preventive', percent: '100%', state: 'covered', deductibleWaived: true, detail: 'Cleanings, exams, X-rays' },
    { category: 'Basic', percent: '80%', state: 'covered', detail: 'Fillings, extractions' },
    { category: 'Major', percent: '50%', state: 'covered', detail: 'Crowns, bridges, dentures' },
    { category: 'Orthodontics', percent: '50%', state: 'covered', detail: 'Lifetime max $2,000 · $2,000 remaining · all ages' },
    { category: 'Implants', percent: '50%', state: 'covered', detail: 'Subject to missing tooth clause' },
    { category: 'Sealants', state: 'unknown', detail: 'Payer did not return a benefit for this service' },
  ],
  limitations: [
    { label: 'Ortho lifetime max', detail: '$2,000 per member, paid in quarterly instalments' },
    { label: 'Missing tooth clause', detail: 'Applies to implants and bridges' },
  ],
  frequency: [
    {
      group: 'Preventive',
      percent: '100%',
      rows: [
        { procedure: 'Prophylaxis (D1110)', limit: '2 per calendar year', last: 'Jan 22, 2026', next: 'Eligible now', status: 'Eligible', eligible: true },
      ],
    },
    {
      group: 'Orthodontics',
      percent: '50%',
      rows: [
        { procedure: 'Aligner check (D8670)', limit: '1 per 90 days', last: 'May 11, 2026', next: 'Aug 9, 2026', status: 'Eligible', eligible: true },
      ],
    },
  ],
  preauth: [
    { id: 'PA-77120', procedure: 'Invisalign — comprehensive (D8090)', status: 'approved', detail: 'Approved Feb 2, 2026 · $2,000 allocated' },
  ],
};

const JAMES: PiV2Bundle = {
  plans: [JAMES_PLAN],
  visit: {
    appointment: appointmentIn(6, 16, 0, 'Dr. Okafor', 'Invisalign check'),
    opportunities: [
    {
      id: 'op-j1',
      itemType: 'UNSCHEDULED_TREATMENT',
      title: 'Ortho benefit available',
      tier: 'T1',
      detail: '$2,000 lifetime orthodontic benefit remaining · pre-auth already approved',
      moneyLabel: 'Est. insurance may pay',
      moneyText: '$2,000',
      script: 'Your plan includes orthodontic coverage, and we have approval on file for it.',
      icon: 'sparkle',
    },
    {
      id: 'op-j2',
      itemType: 'HYGIENE_RECALL',
      title: 'Cleaning eligible now',
      tier: 'T2',
      detail: 'Covered at 100% · last cleaning Jan 22, 2026',
      moneyLabel: 'Est. patient cost',
      money: 0,
      script: 'Your cleaning is covered in full — shall I find you a time?',
      icon: 'shield',
    },
    ],
    estimate: {
      estimateIsReal: true,
      lines: [
        { code: 'D8670', label: 'Periodic ortho treatment visit', officeFee: 250, insuranceEstimate: 125, patientEstimate: 125 },
      ],
      totals: { officeFee: 250, insuranceEstimate: 125, patientEstimate: 125 },
      script: 'Your estimated portion for this visit is $125.',
    },
  },
  treatmentPlan: {
    total: 5400,
    insurance: 2000,
    patient: 3400,
    rows: [
      { procedure: 'Invisalign — comprehensive (D8090)', total: 5400, insurance: 2000, patient: 3400, status: 'In progress', scheduled: true },
    ],
  },
  claims: [
    { id: 'CLM-690112', date: 'Feb 2, 2026', procedure: 'Invisalign — comprehensive (D8090)', billed: 5400, allowed: 4800, insurance: 500, patient: 4300, status: 'paid', statusLabel: 'Paid — instalment 1' },
    { id: 'CLM-711890', date: 'May 11, 2026', procedure: 'Aligner check (D8670)', billed: 250, allowed: 210, insurance: 105, patient: 105, status: 'paid', statusLabel: 'Paid' },
  ],
};

/** Three plans — the tertiary path. */
const SARA: PiV2Bundle = {
  plans: [
    {
      id: 'pl-aetna',
      role: 'primary',
      carrier: 'Aetna',
      planName: 'Dental PPO 1500',
      planType: 'PPO',
      inNetwork: true,
      memberId: 'AE-118220934',
      group: '77310',
      subscriber: 'Sara Malik',
      relationship: 'Self',
      effective: 'Mar 1, 2026',
      carrierPhone: '(877) 238-6200',
      accent: '#7B4FCF',
      eligibility: { state: 'ok', checkedAt: 'Aug 15, 2026' },
      benefits: {
        annualMax: 1500, used: 0, remaining: 1500, deductible: 50, deductibleMet: 0,
        familyDeductible: 150, familyMet: 0, planYearEnd: 'Dec 31, 2026', daysLeftInPlanYear: 138,
      },
      coverage: [
        { category: 'Preventive', percent: '100%', state: 'covered', deductibleWaived: true, detail: 'Cleanings, exams, X-rays' },
        { category: 'Basic', percent: '80%', state: 'covered', detail: 'Fillings, extractions' },
        { category: 'Major', percent: '50%', state: 'covered', detail: '6-month waiting period — not yet served' },
        { category: 'Orthodontics', state: 'not_covered', detail: 'Adult ortho excluded' },
        { category: 'Implants', state: 'not_covered', detail: 'Plan exclusion' },
        { category: 'Whitening', state: 'not_covered', detail: 'Cosmetic — patient pays 100%' },
      ],
      limitations: [
        { label: 'Waiting period', detail: 'Major services covered from Sep 1, 2026' },
        { label: 'New enrolment', detail: 'Annual max not prorated for a mid-year start' },
      ],
      frequency: [
        {
          group: 'Preventive', percent: '100%',
          rows: [{ procedure: 'Prophylaxis (D1110)', limit: '2 per calendar year', next: 'Eligible now', status: 'Eligible', eligible: true }],
        },
      ],
      preauth: [],
    },
    {
      id: 'pl-guardian',
      role: 'secondary',
      carrier: 'Guardian',
      planName: 'DentalGuard Preferred',
      planType: 'PPO',
      inNetwork: false,
      memberId: 'GU-559102',
      group: '20455',
      subscriber: 'Omar Malik',
      relationship: 'Spouse',
      effective: 'Jan 1, 2026',
      carrierPhone: '(800) 541-7846',
      accent: '#2F6FA8',
      eligibility: { state: 'ok', checkedAt: 'Aug 15, 2026' },
      benefits: {
        annualMax: 1000, used: 0, remaining: 1000, deductible: 75, deductibleMet: 0,
        familyDeductible: 225, familyMet: 0, planYearEnd: 'Dec 31, 2026', daysLeftInPlanYear: 138,
      },
      coverage: [
        { category: 'Preventive', percent: '80%', state: 'covered', detail: 'Out-of-network rate applies' },
        { category: 'Basic', percent: '60%', state: 'covered', detail: 'Out-of-network rate applies' },
        { category: 'Major', percent: '50%', state: 'covered', detail: 'No waiting period' },
        { category: 'Orthodontics', state: 'unknown', detail: 'Payer did not return a benefit for this service' },
        { category: 'Implants', state: 'unknown', detail: 'Payer did not return a benefit for this service' },
        { category: 'Whitening', state: 'not_covered', detail: 'Cosmetic exclusion' },
      ],
      limitations: [
        { label: 'Out of network', detail: 'Patient responsible for the balance above the plan allowance' },
        { label: 'Coordination', detail: 'Non-duplication — pays only what primary did not' },
      ],
      frequency: [],
      preauth: [],
    },
    {
      id: 'pl-discount',
      role: 'tertiary',
      carrier: 'CareCredit Dental Savings',
      planName: 'Savings Plan — Tier 2',
      planType: 'Discount',
      inNetwork: true,
      memberId: 'CC-8891204',
      group: '—',
      subscriber: 'Sara Malik',
      relationship: 'Self',
      effective: 'Jun 12, 2026',
      carrierPhone: '(866) 893-7864',
      accent: '#4E8C6A',
      eligibility: { state: 'never' },
      benefits: {
        annualMax: 0, used: 0, remaining: 0, deductible: 0, deductibleMet: 0,
        familyDeductible: 0, familyMet: 0, planYearEnd: 'Jun 11, 2027', daysLeftInPlanYear: 300,
      },
      coverage: [
        { category: 'Preventive', state: 'unknown', detail: 'Discount plan — no eligibility response to read' },
        { category: 'Basic', state: 'unknown', detail: 'Discount plan — no eligibility response to read' },
        { category: 'Major', state: 'unknown', detail: 'Discount plan — no eligibility response to read' },
        { category: 'Orthodontics', state: 'unknown', detail: 'Discount plan — no eligibility response to read' },
        { category: 'Implants', state: 'unknown', detail: 'Discount plan — no eligibility response to read' },
        { category: 'Whitening', state: 'unknown', detail: 'Discount plan — no eligibility response to read' },
      ],
      limitations: [
        { label: 'Not insurance', detail: 'A discount schedule, not a benefit — nothing is paid on the patient’s behalf' },
      ],
      frequency: [],
      preauth: [],
    },
  ],
  coordinationNote:
    'Aetna is primary and Guardian is secondary under non-duplication. The CareCredit savings plan is a discount schedule, not insurance — it never pays and is applied at the desk.',
  /**
   * Her first visit is 9 days out — outside the window — so `visit` is absent
   * and the page starts at the plan cards. The opportunities the engine would
   * produce for that visit are not shown early: an opportunity is a thing to
   * say to someone about to be in the chair, and there is no such moment yet.
   */
  treatmentPlan: { total: 0, insurance: 0, patient: 0, rows: [] },
  claims: [],
};

/** Failed eligibility — the estimate gate renders its honest empty state. */
const TOM: PiV2Bundle = {
  plans: [
    {
      id: 'pl-delta',
      role: 'primary',
      carrier: 'Delta Dental',
      planName: 'PPO Individual',
      planType: 'PPO',
      inNetwork: true,
      memberId: 'DD-201884777',
      group: '10022',
      subscriber: 'Tom Rivera',
      relationship: 'Self',
      effective: 'Jan 1, 2023',
      carrierPhone: '(800) 765-6003',
      accent: '#B4553F',
      eligibility: { state: 'failed', reason: 'payer returned "member not found"' },
      benefits: {
        annualMax: 1500, used: 900, remaining: 600, deductible: 50, deductibleMet: 50,
        familyDeductible: 150, familyMet: 50, planYearEnd: 'Dec 31, 2026', daysLeftInPlanYear: 138,
      },
      coverage: [
        { category: 'Preventive', percent: '100%', state: 'covered', deductibleWaived: true, detail: 'Last known benefit — not re-confirmed' },
        { category: 'Basic', percent: '80%', state: 'covered', detail: 'Last known benefit — not re-confirmed' },
        { category: 'Major', percent: '50%', state: 'covered', detail: 'Last known benefit — not re-confirmed' },
        { category: 'Orthodontics', state: 'unknown', detail: 'No eligibility response' },
        { category: 'Implants', state: 'unknown', detail: 'No eligibility response' },
        { category: 'Sealants', state: 'unknown', detail: 'No eligibility response' },
      ],
      limitations: [
        { label: 'Member ID', detail: 'Payer does not recognise this member ID — confirm the card with the patient' },
      ],
      frequency: [
        {
          group: 'Preventive', percent: '100%',
          rows: [{ procedure: 'Prophylaxis (D1110)', limit: '2 per calendar year', last: 'Nov 20, 2025', next: 'Eligible now', status: 'Eligible', eligible: true }],
        },
      ],
      preauth: [],
    },
  ],
  visit: {
    appointment: appointmentIn(4, 9, 0, 'Dr. Carter', 'Hygiene'),
    opportunities: [
    {
      id: 'op-t1',
      itemType: 'HYGIENE_RECALL',
      title: 'Hygiene recall 3 months overdue',
      tier: 'T1',
      detail: 'Last cleaning Nov 20, 2025 · two visits available per year',
      moneyLabel: 'Est. production',
      money: 210,
      script: 'It has been a while since your last cleaning — shall I find you a time?',
      icon: 'clipboard',
    },
    {
      id: 'op-t2',
      itemType: 'FINANCIAL_AWARENESS',
      title: 'Outstanding balance $340',
      tier: 'T2',
      detail: 'Patient asked to be called before any card is run',
      moneyLabel: 'Balance',
      money: 340,
      icon: 'dollar',
    },
    ],
    estimate: {
      estimateIsReal: false,
      unavailableReason:
        'Eligibility could not be confirmed — the payer does not recognise this member ID. Re-verify before quoting any figure.',
      lines: [],
      totals: { officeFee: 0, insuranceEstimate: 0, patientEstimate: 0 },
    },
  },
  treatmentPlan: {
    total: 1250,
    insurance: 0,
    patient: 1250,
    rows: [
      { procedure: 'Composite filling (D2392)', total: 350, insurance: 0, patient: 350, status: 'Not scheduled', scheduled: false },
      { procedure: 'Night guard (D9944)', total: 900, insurance: 0, patient: 900, status: 'Not scheduled', scheduled: false },
    ],
  },
  claims: [
    { id: 'CLM-604412', date: 'Nov 27, 2025', procedure: 'Composite filling (D2392)', billed: 350, allowed: 280, insurance: 224, patient: 56, status: 'paid', statusLabel: 'Paid' },
    { id: 'CLM-598120', date: 'Nov 20, 2025', procedure: 'Prophylaxis (D1110)', billed: 145, allowed: 118, insurance: 118, patient: 0, status: 'paid', statusLabel: 'Paid' },
  ],
};

export const PP_INSURANCE_V2_BY_PATIENT: Record<string, PiV2Bundle> = {
  'amara-reza': AMARA,
  'james-chen': JAMES,
  'sara-malik': SARA,
  'tom-rivera': TOM,
  // Orville Allison — p001 (amendment 34 §4). Reuses James's Delta Dental bundle
  // so his Insurance tab reads Delta Dental, not Todd's Sun Life.
  p001: JAMES,
};
